import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { DynamicImageWrapper } from '@/components/molecules/DynamicImageWrapper/DynamicImageWrapper';
import { InfoDisplay } from '@/components/molecules/InfoDisplay';
import { priceFormatter } from '@/lib/priceFormatter';
import { TrimStyleWithSpecs } from '@/lib/schema/build/types';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useCallback } from 'react';

interface TrimComparisonListProps {
  trims: TrimStyleWithSpecs[];
  selectedTrims: TrimStyleWithSpecs[];
  addTrim: (newTrim: TrimStyleWithSpecs) => void;
  clearTrim: (trimId: number) => void;
  atLimit: boolean;
}

export const TrimSelectionList: React.FC<TrimComparisonListProps> = ({
  trims,
  selectedTrims,
  addTrim,
  clearTrim,
  atLimit,
}) => {
  const handleSelectTrim = useCallback(
    (trim: TrimStyleWithSpecs, isSelected: boolean) => {
      if (isSelected) {
        clearTrim(trim.trimId);
      }
      addTrim(trim);
    },
    [addTrim, clearTrim]
  );
  return (
    <div>
      <div className="flex flex-row justify-start gap-3 overflow-x-scroll px-5 lg:px-10">
        {trims.map((trim) => {
          const isSelected = selectedTrims.some(
            (selected) => selected.trimId === trim.trimId
          );
          return (
            <div
              key={trim.trimId}
              className="flex h-full w-full min-w-[246px] flex-col justify-between gap-[20px] rounded border-2 border-transparent px-[10px] py-[20px]"
            >
              <div className="flex h-full w-full flex-col items-center justify-center px-2">
                <DynamicImageWrapper
                  make={trim.make}
                  src={trim.image}
                  alt={`${trim.trim} image`}
                  ratio={0.7}
                  priority={true}
                />
              </div>
              <div className={'flex flex-col gap-[20px]'}>
                <div>
                  <span className="flex items-center text-subtitle16px font-light text-[#7F7F7F]">
                    {trim.trim}
                  </span>
                </div>
                <div className={'grid grid-cols-2 gap-[10px]'}>
                  <InfoDisplay
                    main={{ text: priceFormatter(trim.msrp) }}
                    title={'MSRP'}
                  />
                  <InfoDisplay
                    main={{
                      text: trim.range?.value || 0,
                      postFix: trim.range?.unit,
                    }}
                    title={'Max Range'}
                  />
                </div>
                {isSelected ? (
                  <Button
                    onClick={() => handleSelectTrim(trim, isSelected)}
                    variant={ButtonVariants.Primary}
                    disabled={!isSelected && atLimit}
                    icon={{
                      icon: <CheckIcon width={20} height={20} />,
                      position: 'left',
                    }}
                    aria-label="Select trim button"
                  >
                    Selected
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSelectTrim(trim, isSelected)}
                    variant={ButtonVariants.Secondary}
                    disabled={!isSelected && atLimit}
                    aria-label="Select trim button"
                  >
                    Compare
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
